<template>
  <!-- container element for the page -->
  <div class="page-influencer-discovery large-page">
    <!-- Show toggle buttons and search input -->
    <div class="d-flex justify-center justify-md-end flex-wrap">
      <!-- show the switches for different pages -->
      <!-- <page-switch :items="navigationItems" :default-index="1" /> -->

      <div class="d-flex mt-3 mt-sm-0">
        <!-- show the input for searching terms -->
        <div class="search-input">
          <relevance-search
            :platform="selectedPlatform"
          />
        </div>
        <!-- the select options for different platforms -->
        <platform-selector
          :value="selectedPlatform"
          @input="handlePlatformChange"
          class="platform-selector"
        />
      </div>
    </div>

    <div :key="reloadKey">
      <word-cloud
        :platform="selectedPlatform"
      />

      <div class="pt-6 pb-3 d-flex justify-end">
        <filter-verified />
      </div>
      <div class="pb-3 d-flex justify-end">
        <filter-has-audience-data />
      </div>
      <div class="pb-6 d-flex justify-end">
        <filter-exclude-private-accounts />
      </div>

      <!-- the filters card -->
      <v-card
        class="pa-4 box-shadow-md"
        outlined
      >
        <v-card-text>
          <!-- show all possible options for search/filter -->
          <filter-options :platform="selectedPlatform" />

          <!-- view the chips for selected/applied filters -->
          <applied-filters
            :filters="appliedFilters"
            @remove="handleRemove"
          />
        </v-card-text>

        <!--
        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            color="primary"
            depressed
            @click="doSearch"
            :disabled="isLoading"
          >
            <v-icon left>search</v-icon>
            Search
          </v-btn>
        </v-card-actions>
        -->
      </v-card>
    </div>

    <template v-if="shouldShowResults">
      <results-table
        :platform="selectedPlatform"
        :results="results"
        @export="doSearch({ resetPage: false })"
      />

      <div
        v-if="shouldShowLoadMore && results && results.accounts.length"
        class="d-flex justify-center py-4"
      >
        <v-btn
          color="primary"
          class="shadow--primary"
          depressed
          @click="shouldShowDialog = true"
        >
          Load more
        </v-btn>
      </div>

      <div
        id="intersect-detector"
        ref="intersectDetector"
        v-intersect="handleIntersect"
      ></div>
    </template>

    <!-- dialog for confirmation about pagination -->
    <v-dialog
      v-model="shouldShowDialog"
      width="500"
    >
      <v-card>
        <v-card-title>
          Confirmation
        </v-card-title>

        <v-card-text>
          To See this report
          <br />
          Service Cost
          <b>
            {{ unlockCost }} credits
          </b>
          <br />
          Your Balance
          <b>
            {{ availableCredits }} credits
          </b>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn
            text
            color="primary"
            @click="shouldShowDialog = false"
          >
            Cancel
          </v-btn>

          <v-btn
            depressed
            color="primary"
            @click="paginateSearch"
          >
            Proceed
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
const PlatformSelector = () => import("@/blocks/common/selectors/PlatformSelector.vue")
const FilterVerified = () => import("@/blocks/influencer-discovery/FilterVerified.vue")
const FilterHasAudienceData = () => import("@/blocks/influencer-discovery/FilterHasAudienceData.vue")
const FilterExcludePrivateAccounts = () => import("@/blocks/influencer-discovery/FilterExcludePrivateAccounts.vue")
// const PageSwitch = () => import("@/blocks/common/PageSwitch.vue")

const RelevanceSearch = () => import("@/components/influencer-discovery/RelevanceSearch.vue")
const FilterOptions = () => import("@/components/influencer-discovery/FilterOptions.vue")
const WordCloud = () => import("@/components/influencer-discovery/WordCloud.vue")
const AppliedFilters = () => import("@/components/common/AppliedFilters.vue")

const ResultsTable = () => import("@/components/influencer-discovery/ResultsTable.vue")

export default {
  name: "InfluencerDiscovery",

  components: {
    // PageSwitch,
    FilterVerified,
    FilterHasAudienceData,
    FilterExcludePrivateAccounts,
    PlatformSelector,
    FilterOptions,
    AppliedFilters,
    WordCloud,
    ResultsTable,
    RelevanceSearch
},

  data: () => ({
    requestCompletedAt: null,
    shouldShowDialog: false,
    shouldShowResults: false,
    shouldShowLoadMore: false,
    isLoading: false,
    isLoadingSearchResults: true,

    reloadKey: Symbol(),

    // used to cancel networkRequests
    abortControllers: [],

    navigationItems: [
      {
        text: "Influencer Insight",
        link: {
          name: "InfluencerInsight"
        }
      },
      {
        text: "Influencer Discovery",
        link: {
          name: "InfluencerDiscovery"
        }
      }
    ],

    form: {
      platform: "instagram",
      page: 1
    },

    // for debounce on search
    _timerId: null
  }),

  computed: {
    selectedPlatform() {
      return this.$store.getters["influencerDiscovery/selectedPlatform"]
    },

    results() {
      return this.$store.getters["influencerDiscovery/results"](this.selectedPlatform)
    },

    unlockCost() {
      return 0.04 * (this.results?.accounts?.filter((item) => item.is_locked) || []).length
    },

    profileData() {
      return this.$store.getters["auth/profile"]
    },

    availableCredits() {
      return this.profileData ? this.profileData.balance / 100 : 0
    },

    // get the applied filters from vuex store
    appliedFilters() {
      return this.$store.getters[`influencerDiscovery/filters`]
        .sort((a, b) => a.sort - b.sort)
    },
  },

  methods: {
    /**
     * Show a loader for 2 seconds and then show the posts grid
     *
     * @param {Boolean} updateTimestamp - if true, it'd update the timestamp after request
     */
    doSearch({ updateTimestamp = false, resetPage = true } = { updateTimestamp: false, resetPage: true }) {
      // cancel pending call
      if (this._timerId) {
        clearTimeout(this._timerId)
      }

      // delay new call 500ms
      this._timerId = setTimeout(async () => {
        // cancel pending network requests
        for (const item of this.abortControllers) {
          item.controller.abort()
        }

        // If we should reset the page counter to 1
        if (resetPage) {
          this.form.page = 1
        }

        // don't let multiple parallel requests to be initiated
        this.isLoading = true

        // make network requests in vuex store
        const id = Symbol()
        const controller = new AbortController()

        // push this controller to the list
        this.abortControllers.push({ id, controller })

        await this.$store.dispatch("influencerDiscovery/searchAccounts", {
          platform: this.selectedPlatform,
          page: this.form.page,

          // send the signal from abortController
          signal: controller.signal
        })

        // remove the controller from cancellable array
        const index = this.abortControllers.findIndex((item) => item.id === id)
        this.abortControllers.splice(index, 1)

        // since the request is complete, enable the submit button
        this.isLoading = false
        this.shouldShowResults = true

        if (updateTimestamp) {
          this.requestCompletedAt = Date.now()

          // wait for a few seconds to show load more button if needed
          setTimeout(() => {
            const el = document.getElementById("intersect-detector")

            if (el && el.offsetTop < window.innerHeight) {
              this.shouldShowLoadMore = true
            }
            else {
              this.shouldShowLoadMore = false
            }
          }, 500)
        }
      }, 500)
    },

    // whenever platform is switched, reset the filters and the shouldShow state
    handlePlatformChange(value) {
      this.shouldShowResults = false
      this.reloadKey = Symbol()
      this.form.page = 1

      this.$store.dispatch("influencerDiscovery/setSelectedPlatform", value)
      this.$store.dispatch("influencerDiscovery/resetFilters")

      // also emit a global window event that the platform changed
      window.dispatchEvent(new CustomEvent('platformChange', {
        detail: {
          module: "influencerDiscovery",
          origin: "InfluencerDiscovery/handlePlatformChange",
          platform: this.selectedPlatform
        }
      }))
    },

    handleIntersect(entries, observer, isIntersecting) {
      // if the function is called under 2 seconds after it first loaded content, don't continue
      if ((Date.now() - this.requestCompletedAt) <= 1000) return false

      // stop execution if user didn't scroll down
      if (!isIntersecting) return false

      // if a request is being made, don't continue
      if (this.isLoading) return false

      // if there's no data right now, don't ask for next page
      if (!this.results.accounts.length) return false

      // If there's nothing to be charged
      if (this.unlockCost === 0) {
        this.paginateSearch()
      }
      // Otherwise show the dialog
      else {
        this.shouldShowDialog = true
      }
    },

    // If this was called, fetch the results
    paginateSearch() {
      if (this.isLoading) return false

      this.shouldShowDialog = false

      this.form.page++
      this.doSearch({ resetPage: false })
    },

    /**
     * When the applied filter's remove icon is clicked
     *
     * @param {Object} data
     */
    handleRemove(data) {
      this.$store.dispatch(`influencerDiscovery/removeFilter`, data.id)
    }
  },

  mounted() {
    // Listen for the inputs from relevance-search component
    window.addEventListener("triggerSearch", (e) => {
      // If it's not the one we want, don't execute the code
      if (e.detail.module !== "influencerDiscovery") return

      // Make it search
      this.doSearch()
    })

    // fetch for the first time
    this.doSearch({ updateTimestamp: true })
  }
}
</script>

<style lang="stylus">
// to contain the entire box structure in large viewport
.page-influencer-discovery
  .search-input
    @media (min-width 960px)
      width 18em

    .v-input__slot
      height 48px
      border-top-right-radius 0
      border-bottom-right-radius 0

    .v-label
      padding-top 4px

    // to fix the label that's cropped at the bottom
    .v-select__slot label
      overflow visible !important

  .platform-selector
    .v-input__slot
      border-top-left-radius 0
      border-bottom-left-radius 0
</style>
